import { Campaign, Task } from '../../types';

export interface TaskSessionActor {
  uid: string;
  email?: string | null;
  displayName?: string | null;
}

export type DraftSaveKind = 'writer' | 'reviewer';

export interface DraftSaveContext {
  editable: boolean;
  kind: DraftSaveKind;
  actor: TaskSessionActor | null;
}

export interface TaskSessionPorts {
  subscribeTask: (
    taskId: string,
    onChange: (task: Task | null) => void,
    onError: (error: Error) => void,
  ) => () => void;
  subscribeCampaign: (
    campaignId: string,
    onChange: (campaign: Campaign | null) => void,
    onError: (error: Error) => void,
  ) => () => void;
  saveTaskDraft: (
    taskId: string,
    actor: TaskSessionActor,
    result: Record<string, unknown>,
  ) => Promise<void>;
  saveReviewTaskResult: (
    taskId: string,
    result: Record<string, unknown>,
  ) => Promise<void>;
}

export interface TaskSessionClock {
  setTimeout: (callback: () => void, delayMs: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

export interface TaskSessionSnapshot {
  busy: boolean;
  error: string | null;
  task: Task | null;
  campaign: Campaign | null;
  draftResult: unknown;
  draftInitializedFor: string | null;
  hasUnsavedChanges: boolean;
  saveError: string | null;
}

export interface TaskSessionControllerOptions {
  ports: TaskSessionPorts;
  clock?: TaskSessionClock;
  autosaveDelayMs?: number;
  emptyResultForTask: (task: Task) => unknown;
}

interface PendingSave {
  taskId: string;
  actor: TaskSessionActor | null;
  kind: DraftSaveKind;
  result: unknown;
  revision: number;
}

const realClock: TaskSessionClock = {
  setTimeout: (callback, delayMs) => setTimeout(callback, delayMs),
  clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

const initialSnapshot: TaskSessionSnapshot = {
  busy: true,
  error: null,
  task: null,
  campaign: null,
  draftResult: null,
  draftInitializedFor: null,
  hasUnsavedChanges: false,
  saveError: null,
};

function sameActor(left: TaskSessionActor | null, right: TaskSessionActor | null): boolean {
  if (left === right) return true;
  if (!left || !right) return false;
  return left.uid === right.uid
    && left.email === right.email
    && left.displayName === right.displayName;
}

function asError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

/**
 * Content identity for a task result, used only to tell "the server moved on"
 * from "the same value came round again". Results are JSON data, so
 * serializing is enough: two snapshots of the same doc stringify in the same
 * field order, and a locally rebuilt draft that differs only in key order can
 * cost at most one no-op resync of an already-clean draft.
 */
function resultFingerprint(result: unknown): string {
  return JSON.stringify(result ?? null);
}

/**
 * Owns the task-detail session's remote lifecycle and draft persistence.
 *
 * The controller deliberately knows only domain values and injected ports. It
 * is therefore usable from a route hook, a test harness, or a future page
 * composition without importing Firebase or React.
 */
export class TaskSessionController {
  private readonly ports: TaskSessionPorts;

  private readonly clock: TaskSessionClock;

  private readonly autosaveDelayMs: number;

  private emptyResultForTask: (task: Task) => unknown;

  private snapshot: TaskSessionSnapshot = { ...initialSnapshot };

  private listeners = new Set<() => void>();

  private taskUnsubscribe: (() => void) | null = null;

  private campaignUnsubscribe: (() => void) | null = null;

  private taskId: string | undefined;

  private actor: TaskSessionActor | null = null;

  private initialized = false;

  private disposed = false;

  private lifecycleToken = 0;

  private campaignId: string | null = null;

  private autosaveHandle: unknown = null;

  private draftRevision = 0;

  private lastSaveContext: DraftSaveContext | null = null;

  constructor(options: TaskSessionControllerOptions) {
    this.ports = options.ports;
    this.clock = options.clock ?? realClock;
    this.autosaveDelayMs = options.autosaveDelayMs ?? 2000;
    this.emptyResultForTask = options.emptyResultForTask;
  }

  getSnapshot = (): TaskSessionSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  updateContext({
    taskId,
    actor,
    emptyResultForTask,
  }: {
    taskId: string | undefined;
    actor: TaskSessionActor | null;
    emptyResultForTask?: (task: Task) => unknown;
  }): void {
    if (emptyResultForTask) this.emptyResultForTask = emptyResultForTask;
    const taskChanged = taskId !== this.taskId;
    const actorChanged = !sameActor(actor, this.actor);
    const pending = this.capturePendingSave();
    this.taskId = taskId;
    this.actor = actor;
    if (!taskChanged && !actorChanged) return;

    this.cancelAutosave();
    if (pending) {
      void this.persistPending(pending).catch(() => {
        // A context switch cannot surface an old task's save failure in the
        // new task. The next explicit save will report its own error.
      });
    }

    if (this.initialized) this.stopSubscriptions();
    this.draftRevision += 1;
    this.snapshot = {
      ...this.snapshot,
      busy: !!taskId,
      error: null,
      task: taskChanged ? null : this.snapshot.task,
      campaign: taskChanged ? null : this.snapshot.campaign,
      draftResult: taskChanged ? null : this.snapshot.task?.result ?? null,
      draftInitializedFor: null,
      hasUnsavedChanges: false,
      saveError: null,
    };
    this.lastSaveContext = null;
    this.campaignId = taskChanged ? null : this.campaignId;
    this.emit();

    // A logged-out/logged-in user must not inherit an unsaved draft from the
    // previous identity, but the current persisted task remains usable.
    if (actorChanged && !taskChanged && this.snapshot.task) {
      this.seedDraft(this.snapshot.task);
    }
    if (this.initialized) this.startSubscriptions();
  }

  initialize(): void {
    this.disposed = false;
    if (this.initialized) return;
    this.initialized = true;
    this.startSubscriptions();
  }

  async dispose(): Promise<void> {
    const pending = this.capturePendingSave();
    this.cancelAutosave();
    this.stopSubscriptions();
    this.initialized = false;
    this.disposed = true;
    if (pending) {
      try {
        await this.persistPending(pending);
      } catch (error) {
        this.snapshot = {
          ...this.snapshot,
          saveError: asError(error).message,
        };
        this.emit();
      }
    }
  }

  setDraftResult(next: unknown, context: DraftSaveContext): void {
    if (!this.taskId || this.snapshot.draftInitializedFor !== this.taskId) return;
    this.draftRevision += 1;
    this.snapshot = {
      ...this.snapshot,
      draftResult: next,
      saveError: null,
      hasUnsavedChanges: context.editable && (context.kind === 'reviewer' || !!context.actor),
    };
    this.lastSaveContext = context;
    this.emit();
    if (!this.snapshot.hasUnsavedChanges) return;

    const pending: PendingSave = {
      taskId: this.taskId,
      actor: context.actor,
      kind: context.kind,
      result: next,
      revision: this.draftRevision,
    };
    this.lastSaveContext = context;
    this.cancelAutosave();
    this.autosaveHandle = this.clock.setTimeout(() => {
      this.autosaveHandle = null;
      void this.persistPending(pending)
        .then(() => this.markPersisted(pending))
        .catch((error) => this.markSaveError(pending, error));
    }, this.autosaveDelayMs);
  }

  cancelAutosave(): void {
    if (this.autosaveHandle === null) return;
    this.clock.clearTimeout(this.autosaveHandle);
    this.autosaveHandle = null;
  }

  /**
   * Cancel a scheduled autosave when a higher-level action is about to carry
   * the current draft itself (for example, submit).  The draft must not then
   * be flushed again by route cleanup, because a writer draft save would move
   * a just-submitted task back to `in_progress`.
   */
  discardPendingAutosave(): void {
    this.cancelAutosave();
    if (!this.snapshot.hasUnsavedChanges) return;
    this.draftRevision += 1;
    this.lastSaveContext = null;
    this.snapshot = {
      ...this.snapshot,
      hasUnsavedChanges: false,
      saveError: null,
    };
    this.emit();
  }

  async saveDraft(context: DraftSaveContext): Promise<void> {
    if (!context.editable || !this.taskId) return;
    this.lastSaveContext = context;
    this.cancelAutosave();
    const pending: PendingSave = {
      taskId: this.taskId,
      actor: context.actor,
      kind: context.kind,
      result: this.snapshot.draftResult,
      revision: this.draftRevision,
    };
    await this.persistPending(pending);
    this.markPersisted(pending);
  }

  async flushForReload(context: DraftSaveContext): Promise<void> {
    if (!context.editable || !this.snapshot.hasUnsavedChanges) return;
    this.lastSaveContext = context;
    this.cancelAutosave();
    const pending: PendingSave = {
      taskId: this.taskId || '',
      actor: context.actor,
      kind: context.kind,
      result: this.snapshot.draftResult,
      revision: this.draftRevision,
    };
    await this.persistPending(pending);
    this.markPersisted(pending);
  }

  applyTaskSnapshot(task: Task | null): void {
    if (!this.taskId || (task && task.id !== this.taskId)) return;
    if (!task) {
      this.snapshot = {
        ...this.snapshot,
        task: null,
        campaign: null,
        draftResult: null,
        draftInitializedFor: null,
        hasUnsavedChanges: false,
      };
      this.emit();
      return;
    }
    this.snapshot = {
      ...this.snapshot,
      task,
      busy: false,
      error: null,
      draftResult: task.result ?? this.emptyResultForTask(task),
      draftInitializedFor: task.id,
      hasUnsavedChanges: false,
      saveError: null,
    };
    this.draftRevision += 1;
    this.emit();
  }

  private startSubscriptions(): void {
    const taskId = this.taskId;
    if (!taskId) {
      this.snapshot = {
        ...this.snapshot,
        busy: false,
        error: 'Missing task id.',
      };
      this.emit();
      return;
    }
    const token = ++this.lifecycleToken;
    this.snapshot = {
      ...this.snapshot,
      busy: true,
      error: null,
    };
    this.emit();
    try {
      this.taskUnsubscribe = this.ports.subscribeTask(
        taskId,
        (task) => {
          if (this.disposed || token !== this.lifecycleToken || taskId !== this.taskId) return;
          this.applyTaskFromSubscription(task);
        },
        (error) => {
          if (this.disposed || token !== this.lifecycleToken || taskId !== this.taskId) return;
          this.snapshot = {
            ...this.snapshot,
            busy: false,
            error: error.message,
          };
          this.emit();
        },
      );
    } catch (error) {
      this.snapshot = {
        ...this.snapshot,
        busy: false,
        error: asError(error).message,
      };
      this.emit();
    }
  }

  /**
   * A task doc can move ahead of an already-seeded draft while the page stays
   * mounted: the writer resubmits after `changes_requested` (the rework
   * reviewer is reassigned by the submit command, so the reviewer never
   * re-claims and never gets a fresh seed), or an admin edits the result
   * during review. Adopting the server's result keeps the review body — and
   * the reviewer's `task.json` download, which serializes the draft — on the
   * latest saved version instead of the round the page was opened on.
   *
   * Only ever done when the local draft is clean: an unsaved or
   * pending-autosave draft is the newer value and must win. Echoes of our own
   * save are no-ops because the draft already matches the incoming result.
   */
  private shouldAdoptRemoteResult(task: Task): boolean {
    if (this.snapshot.hasUnsavedChanges || this.autosaveHandle !== null) return false;
    const incoming = resultFingerprint(task.result);
    if (resultFingerprint(this.snapshot.task?.result) === incoming) return false;
    return resultFingerprint(this.snapshot.draftResult) !== incoming;
  }

  private applyTaskFromSubscription(task: Task | null): void {
    if (!task) {
      this.campaignUnsubscribe?.();
      this.campaignUnsubscribe = null;
      this.campaignId = null;
      this.applyTaskSnapshot(null);
      this.snapshot = {
        ...this.snapshot,
        busy: false,
        error: 'Task not found.',
      };
      this.emit();
      return;
    }
    const shouldSeed = this.snapshot.draftInitializedFor !== task.id;
    const shouldResync = !shouldSeed && this.shouldAdoptRemoteResult(task);
    if (shouldResync) this.draftRevision += 1;
    this.snapshot = {
      ...this.snapshot,
      task,
      busy: false,
      error: null,
      ...(shouldSeed || shouldResync
        ? {
          draftResult: task.result ?? this.emptyResultForTask(task),
          draftInitializedFor: task.id,
          hasUnsavedChanges: false,
          saveError: null,
        }
        : {}),
    };
    this.emit();
    if (task.campaignId !== this.campaignId) this.startCampaignSubscription(task.campaignId);
  }

  private startCampaignSubscription(campaignId: string): void {
    this.campaignUnsubscribe?.();
    this.campaignUnsubscribe = null;
    this.campaignId = campaignId;
    this.snapshot = { ...this.snapshot, campaign: null };
    this.emit();
    const token = this.lifecycleToken;
    try {
      this.campaignUnsubscribe = this.ports.subscribeCampaign(
        campaignId,
        (campaign) => {
          if (this.disposed || token !== this.lifecycleToken || campaignId !== this.campaignId) return;
          this.snapshot = { ...this.snapshot, campaign };
          this.emit();
        },
        (error) => {
          if (this.disposed || token !== this.lifecycleToken || campaignId !== this.campaignId) return;
          this.snapshot = { ...this.snapshot, saveError: error.message };
          this.emit();
        },
      );
    } catch (error) {
      this.snapshot = { ...this.snapshot, saveError: asError(error).message };
      this.emit();
    }
  }

  private seedDraft(task: Task): void {
    this.snapshot = {
      ...this.snapshot,
      draftResult: task.result ?? this.emptyResultForTask(task),
      draftInitializedFor: task.id,
      hasUnsavedChanges: false,
      saveError: null,
    };
    this.emit();
  }

  private stopSubscriptions(): void {
    this.lifecycleToken += 1;
    this.taskUnsubscribe?.();
    this.taskUnsubscribe = null;
    this.campaignUnsubscribe?.();
    this.campaignUnsubscribe = null;
    this.campaignId = null;
  }

  private capturePendingSave(): PendingSave | null {
    if (!this.snapshot.hasUnsavedChanges || !this.taskId) return null;
    const context = this.lastSaveContext;
    if (!context) return null;
    return {
      taskId: this.taskId,
      actor: context.actor,
      kind: context.kind,
      result: this.snapshot.draftResult,
      revision: this.draftRevision,
    };
  }

  private async persistPending(pending: PendingSave): Promise<void> {
    if (pending.kind === 'writer') {
      if (!pending.actor) throw new Error('You must be signed in to save this draft.');
      await this.ports.saveTaskDraft(
        pending.taskId,
        pending.actor,
        pending.result as Record<string, unknown>,
      );
      return;
    }
    await this.ports.saveReviewTaskResult(
      pending.taskId,
      pending.result as Record<string, unknown>,
    );
  }

  private markPersisted(pending: PendingSave): void {
    if (pending.taskId !== this.taskId || pending.revision !== this.draftRevision) return;
    this.snapshot = {
      ...this.snapshot,
      hasUnsavedChanges: false,
      saveError: null,
    };
    this.emit();
  }

  private markSaveError(pending: PendingSave, error: unknown): void {
    if (pending.taskId !== this.taskId || pending.revision !== this.draftRevision) return;
    this.snapshot = {
      ...this.snapshot,
      saveError: asError(error).message,
    };
    this.emit();
  }

  private emit(): void {
    this.listeners.forEach((listener) => listener());
  }
}
